import i18next from "i18next";
import type React from "react";
import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useState,
} from "react";
import { DEFAULT_LANGUAGE, type LanguageKey, languages } from "../i18n";

// Define the context type
interface LanguageContextType {
	currentLanguage: LanguageKey;
	changeLanguage: (lang: LanguageKey) => void;
	languages: typeof languages;
}

// Create the context with a default value
const LanguageContext = createContext<LanguageContextType>({
	currentLanguage: DEFAULT_LANGUAGE,
	changeLanguage: () => {},
	languages,
});

// Storage key for persisting language preference
const LANGUAGE_STORAGE_KEY = "ruralops-language";

// The key this app shipped with by mistake — a copy-paste from another product.
// It is read once so an existing user's choice survives the rename, then dropped
// on mount. Remove once no active browser can still be holding the old key.
const LEGACY_LANGUAGE_STORAGE_KEY = "pilatestime-language";

function readStoredLanguage(): LanguageKey | null {
	for (const key of [LANGUAGE_STORAGE_KEY, LEGACY_LANGUAGE_STORAGE_KEY]) {
		const stored = localStorage.getItem(key) as LanguageKey | null;
		if (stored && languages[stored]) return stored;
	}
	return null;
}

// Provider component
export function LanguageProvider({ children }: { children: React.ReactNode }) {
	// Keep a saved preference; new users start in Brazilian Portuguese.
	const [currentLanguage, setCurrentLanguage] = useState<LanguageKey>(
		() => readStoredLanguage() ?? DEFAULT_LANGUAGE,
	);

	// Change language function - memoized with useCallback
	const changeLanguage = useCallback((lang: LanguageKey) => {
		if (languages[lang]) {
			setCurrentLanguage(lang);
			i18next.changeLanguage(lang);
			localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);

			// Update the html lang attribute
			document.documentElement.lang = lang.replace("_", "-");
		}
	}, []);

	// Initialize language on mount. `changeLanguage` has already written the
	// value under the current key, so the legacy one is now redundant.
	useEffect(() => {
		changeLanguage(currentLanguage);
		localStorage.removeItem(LEGACY_LANGUAGE_STORAGE_KEY);
	}, [currentLanguage, changeLanguage]);

	const value = {
		currentLanguage,
		changeLanguage,
		languages,
	};

	return (
		<LanguageContext.Provider value={value}>
			{children}
		</LanguageContext.Provider>
	);
}

// Custom hook to use the language context
export function useLanguage() {
	const context = useContext(LanguageContext);
	if (context === undefined) {
		throw new Error("useLanguage must be used within a LanguageProvider");
	}
	return context;
}
